import { useState, useEffect, useRef, useCallback } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

// 全局变量，用于跟踪当前吸顶的元素
let currentStickyId: string | null = null;
const stickyCallbacks = new Map<string, (shouldSticky: boolean) => void>();

// 存储所有实例的滚动处理函数，用于手动触发检测
const scrollHandlers = new Map<string, () => void>();

// 滚动锁：防止程序化滚动时触发吸顶检测导致抖动
let isScrollingProgrammatically = false;
let scrollLockTimer: number | null = null;

// 节流函数：使用 requestAnimationFrame 优化性能
const throttleRAF = (callback: () => void) => {
  let rafId: number | null = null;
  let lastTime = 0;
  const minInterval = 8; // 约 120fps，更流畅的检测频率

  return () => {
    const now = Date.now();
    
    // 如果距离上次执行时间小于最小间隔，则跳过
    if (now - lastTime < minInterval) {
      return;
    }

    if (rafId !== null) {
      return;
    }

    rafId = requestAnimationFrame(() => {
      callback();
      lastTime = now;
      rafId = null;
    });
  };
};

/**
 * 获取滚动容器元素
 * @param className - 滚动容器类名
 * @returns 滚动容器元素或 null
 */
const getScrollContainer = (className: string): HTMLElement | null => document.querySelector(className) as HTMLElement;

/**
 * 吸顶配置接口
 */
export interface IStickyConfig {
  /** 唯一标识符，用于管理全局吸顶状态 */
  id: string;
  /** 是否启用吸顶功能 */
  isActive: boolean;
  /** 吸顶偏移量，默认 0 */
  offset?: number;
  /** 滚动容器类名，可选参数 */
  scrollContainerClassName?: string;
}

/**
 * 吸顶逻辑 Hook
 * @param config - 吸顶配置对象
 */
export const useStreamSticky = (config: IStickyConfig) => {
  const {
    id,
    isActive,
    offset = 0,
    scrollContainerClassName,
  } = config;
  const [isSticky, setIsSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 使用 ref 存储当前 sticky 状态，避免闭包陷阱和不必要的状态更新
  const isStickyRef = useRef(false);
  // 使用 ref 存储占位元素高度，避免不必要的状态更新
  const placeholderHeightRef = useRef(0);

  // 计算占位元素高度
  // 关键：必须把 header 在文档流中真实占据的"外边距空间"也算上，
  // 否则当 header 切到 fixed 后，原本 header 的 margin-top/margin-bottom 一并脱离文档流，
  // 占位 div 只用 offsetHeight 会比真实占位少（视觉上表现为"留下大量空白"或"内容上跳"）。
  const calculatePlaceholderHeight = useCallback((): number => {
    const header = headerRef.current;
    if (!header) return 0;
    const rectHeight = header.getBoundingClientRect().height;
    // 读取计算样式中的 margin（仅在需要时执行一次，避免高频重复 reflow）
    const computed = window.getComputedStyle(header);
    const marginTop = parseFloat(computed.marginTop) || 0;
    const marginBottom = parseFloat(computed.marginBottom) || 0;
    return rectHeight + marginTop + marginBottom;
  }, []);

  // 注册/注销全局回调 - 优化：只在状态真正改变时才触发 setState
  useEffect(() => {
    stickyCallbacks.set(id, (shouldSticky: boolean) => {
      // 关键优化：只在状态改变时才调用 setState
      if (isStickyRef.current !== shouldSticky) {
        isStickyRef.current = shouldSticky;
        // 如果要吸顶，计算占位元素高度
        if (shouldSticky) {
          placeholderHeightRef.current = calculatePlaceholderHeight();
        } else {
          placeholderHeightRef.current = 0;
        }
        setIsSticky(shouldSticky);
      }
    });

    return () => {
    // 清理所有回调和处理器
      stickyCallbacks.delete(id);
      scrollHandlers.delete(id);
      // 如果当前是吸顶元素，重置全局状态
      if (currentStickyId === id) {
        currentStickyId = null;
      }
    };
  }, [id, calculatePlaceholderHeight]);

  /**
   * 滚动到当前容器位置（用于吸顶状态下点击收起后滚动回原位）
   * @param offsetTop - 距离顶部的偏移量，默认 25px
   * @param behavior - 滚动行为，默认 'auto'（瞬间滚动，避免 smooth 导致的持续事件触发）
   */
  const scrollToContainer = useCallback(
    (offsetTop = 0, behavior: ScrollBehavior = 'auto') => {
      const scrollContainer = getScrollContainer(scrollContainerClassName);
      const targetElement = containerRef.current;

      if (targetElement && scrollContainer) {
        // 设置滚动锁，防止滚动期间触发吸顶检测
        isScrollingProgrammatically = true;
        
        // 清除之前的定时器
        if (scrollLockTimer !== null) {
          clearTimeout(scrollLockTimer);
        }

        // 计算目标元素相对于滚动容器的位置
        const targetRect = targetElement.getBoundingClientRect();
        const scrollContainerRect = scrollContainer.getBoundingClientRect();
        const relativeTop = targetRect.top - scrollContainerRect.top + scrollContainer.scrollTop;

        // 执行滚动
        scrollContainer.scrollTo({
          top: relativeTop - offsetTop,
          behavior,
        });

        // 根据滚动行为设置不同的解锁延迟
        const unlockDelay = 100;
        
        // 延迟解锁，确保滚动完成后再启用吸顶检测
        scrollLockTimer = window.setTimeout(() => {
          isScrollingProgrammatically = false;
          scrollLockTimer = null;
          
          // 滚动完成后，手动触发一次检测以更新状态
          requestAnimationFrame(() => {
            computeSticky();
          });
        }, unlockDelay);
      }
    },
    [scrollContainerClassName],
  );

  // 核心计算逻辑 - 优化性能
  const computeSticky = useCallback(() => {
    // 如果正在程序化滚动，跳过检测，防止抖动
    if (isScrollingProgrammatically) {
      return;
    }

    const header = headerRef.current;
    const container = containerRef.current;
    if (!header || !container) return;

    // 缓存 DOM 查询结果
    const containerRect = container.getBoundingClientRect();
    const headerHeight = header.offsetHeight;

    // 判断条件（关键：基于容器位置判断，不受占位元素影响）：
    // 1. 容器顶部已经滚动到吸顶位置或更上方（containerRect.top <= offset）
    // 2. 容器底部还在视口内，且留有足够空间显示 header（containerRect.bottom > offset + headerHeight）
    const shouldSticky = containerRect.top <= (offset + headerHeight) && containerRect.bottom > (offset + headerHeight);

    // 如果状态没有变化，直接返回，避免不必要的更新
    if (shouldSticky === isStickyRef.current) {
      // 即使状态相同，也要确保全局状态正确
      if (shouldSticky && currentStickyId !== id) {
        currentStickyId = id;
      }
      return;
    }
    // 如果当前元素应该吸顶
    if (shouldSticky) {
      // 如果已经有其他元素吸顶，先取消它
      if (currentStickyId && currentStickyId !== id) {
        const prevCallback = stickyCallbacks.get(currentStickyId);
        const selfCallback = stickyCallbacks.get(id);
        if (prevCallback && selfCallback) {
          // 批量更新：将取消旧元素和设置新元素的状态更新合并为一次渲染
          // 使用 requestAnimationFrame 确保在下一帧渲染，避免抖动
          requestAnimationFrame(() => {
            currentStickyId = id;
            unstable_batchedUpdates(() => {
              prevCallback(false);
              selfCallback(true);
            });
          });
          return;
        }
      }
      // 设置当前元素为吸顶元素
      if (currentStickyId !== id) {
        currentStickyId = id;
      }
      // 关键优化：只在状态改变时更新
      if (!isStickyRef.current) {
        isStickyRef.current = true;
        placeholderHeightRef.current = calculatePlaceholderHeight();
        setIsSticky(true);
      }
    } else {
      // 如果当前元素不应该吸顶，且它是当前吸顶元素，则取消吸顶
      if (currentStickyId === id) {
        currentStickyId = null;
      }
      // 关键优化：只在状态改变时更新
      if (isStickyRef.current) {
        isStickyRef.current = false;
        placeholderHeightRef.current = 0;
        setIsSticky(false);
      }
    }
  }, [id, offset, calculatePlaceholderHeight]);

  useEffect(() => {
    // 提前检查：如果没有传入滚动容器类名或未启用，则清理状态并返回
    if (!isActive || !scrollContainerClassName) {
      // 清理状态 - 只在状态改变时更新
      if (isStickyRef.current) {
        isStickyRef.current = false;
        placeholderHeightRef.current = 0;
        setIsSticky(false);
      }
      if (currentStickyId === id) {
        currentStickyId = null;
      }
      scrollHandlers.delete(id);
      return;
    }

    const scrollContainer = getScrollContainer(scrollContainerClassName);
    if (!scrollContainer) {
      console.warn('useSteamSticky: 未找到滚动容器', scrollContainerClassName);
      return;
    }

    // 使用节流优化滚动处理
    const handleScroll = throttleRAF(() => {
      computeSticky();
    });

    // 注册滚动处理函数，用于手动触发检测
    scrollHandlers.set(id, handleScroll);

    // 监听滚动容器的 scroll 事件
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    // 初始化调用（不需要节流）
    computeSticky();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      scrollHandlers.delete(id);
      
      // 清理滚动锁定时器
      if (scrollLockTimer !== null) {
        clearTimeout(scrollLockTimer);
        scrollLockTimer = null;
      }
      isScrollingProgrammatically = false;
    };
  }, [id, isActive, offset, computeSticky, calculatePlaceholderHeight, scrollContainerClassName]);

  return {
    isSticky,
    placeholderHeight: placeholderHeightRef.current,
    headerRef,
    containerRef,
    scrollToContainer,
  };
};
